import "./AmMonitor.scss";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import newRequest from '../../../utils/newRequest';
import { GridLoader, ScaleLoader } from "react-spinners";
import { motion } from "framer-motion";
import { MdClose, MdOutlineViewComfyAlt, MdRefresh, } from 'react-icons/md';
import { IoDocumentTextSharp } from 'react-icons/io5';
import CircularButton, { CustomLoadingButton } from "../../../components/ui/CustomButton";
import OnScreenReports from "../../../components/onScreenReports/OnScreenReports";
import {
    Table,
    TablePagination,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
} from "@mui/material";
import { showToast } from "../../../toastService";
import { formatAmountsToDecimals, } from "../../../utils/genericFunctions";
import { CiImageOn } from "react-icons/ci";
import { TbDeviceAnalytics } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import { HiTrendingUp } from "react-icons/hi";
import { FaPeopleGroup } from "react-icons/fa6";
import { GrMoney } from "react-icons/gr";
import { AiOutlineAudit } from "react-icons/ai";
import moment from "moment";
import EnrolledInvestors from "../../../components/enrolledInvestors/EnrolledInvestors";
import FundOverview from "../../../components/fundOverview/FundOverview";

const AmMonitor = () => {
    const { t } = useTranslation();
    const [isProcessing, setIsProcessing] = useState(false);
    const { currentUser } = useSelector((state) => state.user);
    const { currentTheme } = useSelector((state) => state.theme);
    const [isLoading, setIsLoading] = useState(false);
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const navigate = useNavigate();
    const { docid } = useParams();
    const [fundDocId, setFundDocId] = useState(null);
    const [fundData, setFundData] = useState(null);
    const [traceData, setTraceData] = useState([]);
    const [reportsDialogOpen, setReportsDialogOpen] = useState(false);

    const fetchFund = async (doc_id, firstTime = false) => {
        try {
            if (firstTime) {
                setIsLoading(true);
            }
            const res = await newRequest.post("/fund/find/", { fund_docid: doc_id, monitor: "Y", lean: "N" });
            if (res.data) {
                setFundDocId(res.data?._id);
                const formattedData = {
                    ...res.data,
                    nav_per_unit: formatAmountsToDecimals(res.data?.nav_per_unit || 0),
                    minimum_subscription: formatAmountsToDecimals(res.data?.minimum_subscription || 0),
                    aum_cap: formatAmountsToDecimals(res.data?.aum_cap || 0),
                    expected_aum: formatAmountsToDecimals(res.data?.expected_aum || 0),
                    benchmark_target: formatAmountsToDecimals(res.data?.benchmark_target || 0),
                    rebalance_threshold: formatAmountsToDecimals(res.data?.rebalance_threshold || 0),
                    launch_date: res.data?.launch_date ? moment(res.data?.launch_date).format("YYYY-MM-DD") : null,
                };
                // console.log(`formattedData ${JSON.stringify(formattedData, null, 2)}`);
                setFundData(formattedData);
                await fetchTrace(res.data?.fund_code);
            }
        } catch (err) {
            console.log(err);
        } finally {
            if (firstTime) {
                setIsLoading(false);
            }
        }
    };


    const fetchTrace = async (fundCode) => {
        try {
            const res = await newRequest.post(
                "/trace/findDoc",
                {
                    trc_doc_type: "AM Fund",
                    trc_doc_no: fundCode
                }
            );
            setTraceData(res.data);
        } catch (err) {
            console.log(err);
        }
    };


    // top menu 
    const handleTabClick = (index) => {
        setActiveTab(index);
    };
    const [activeTab, setActiveTab] = useState(0);


    const topMenus = [
        {
            menu_key: "overview",
            menu_title: t('overview'),
            menu_icon: <TbDeviceAnalytics />,
            menu_count: 0,
        },
        {
            menu_key: "investors",
            menu_title: t('investors'),
            menu_icon: <FaPeopleGroup />,
            menu_count: 0,
        },
        {
            menu_key: "holdings",
            menu_title: t('holdings'),
            menu_icon: <GrMoney />,
            menu_count: 0,
        },
        {
            menu_key: "compliance",
            menu_title: t('compliance'),
            menu_icon: <MdOutlineViewComfyAlt />,
            menu_count: 0,
        },
        {
            menu_key: "performance",
            menu_title: t('performance'),
            menu_icon: <HiTrendingUp />,
            menu_count: 0,
        },
        {
            menu_key: "audit",
            menu_title: t('audit'),
            menu_icon: <AiOutlineAudit />,
            menu_count: 0,
        },
    ];

    useEffect(() => {
        if (docid) {
            fetchFund(docid, true);
        }
    }, [docid]);


    return (
        <motion.div
            className="am-monitor"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
        >
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <div className="am-monitor-main-wrapper">
                    <div className="am-monitor-navbar">
                        <div className="am-monitor-navbar-left">
                            <span className="main-title"> {fundData?.fund_name}</span>
                            <span className="sub-title"> {fundData?._id}</span>
                        </div>
                        <div className="am-monitor-navbar-right">
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="left"
                                endIcon={<MdRefresh />}
                                onClick={async () => { await fetchFund(fundDocId) }}
                            >
                                {t('refresh')}
                            </CustomLoadingButton>
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="none"
                                endIcon={<IoDocumentTextSharp />}
                                onClick={() => setReportsDialogOpen(true)}>
                                {t('reports')}
                            </CustomLoadingButton>
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="right"
                                endIcon={<MdClose />}
                                onClick={() => navigate(-1)}>
                                {t('close')}
                            </CustomLoadingButton>
                        </div>
                    </div>
                    <div className="content-summary-wrapper">
                        <div className="content-wrapper">
                            <div className="tabs-container">
                                <div className="tab-header">
                                    {topMenus?.map((tab, index) => (
                                        <div
                                            key={index}
                                            className={`${activeTab === index ? 'tab active' : 'tab'}`}
                                            onClick={() => handleTabClick(index)}
                                        >
                                            <span className={`${activeTab === index ? 'tab-icon active' : 'tab-icon'}`}>
                                                {tab.menu_icon}
                                            </span>
                                            <span className={`${activeTab === index ? 'tab-title active' : 'tab-title'}`}>{tab.menu_title}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="monitor-content-details">
                                {activeTab === 0 &&
                                    <motion.div
                                        className="overview"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1, transition: { duration: 1 } }}
                                        exit={{ opacity: 0, transition: { duration: 1 } }}>
                                        <FundOverview fundData={fundData} />
                                    </motion.div>}
                                {activeTab === 1 &&
                                    <motion.div
                                        className="investors"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1, transition: { duration: 1 } }}
                                        exit={{ opacity: 0, transition: { duration: 1 } }}>
                                        <EnrolledInvestors fundDocid={fundDocId} />
                                    </motion.div>}
                                {activeTab === 2 &&
                                    <motion.div
                                        className="holdings"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1, transition: { duration: 1 } }}
                                        exit={{ opacity: 0, transition: { duration: 1 } }}
                                    >
                                        <span>third</span>
                                    </motion.div>}
                                {activeTab === 3 &&
                                    <motion.div
                                        className="compliance"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1, transition: { duration: 1 } }}
                                        exit={{ opacity: 0, transition: { duration: 1 } }}
                                    >
                                        <span>fourth</span>
                                    </motion.div>}
                                {activeTab === 4 &&
                                    <motion.div
                                        className="performance"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1, transition: { duration: 1 } }}
                                        exit={{ opacity: 0, transition: { duration: 1 } }}
                                    >
                                        <span>fifth</span>
                                    </motion.div>}
                                {activeTab === 5 &&
                                    <motion.div
                                        className="audit"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1, transition: { duration: 1 } }}
                                        exit={{ opacity: 0, transition: { duration: 1 } }}
                                    >
                                        <span>sixth</span>
                                    </motion.div>}
                            </div>
                        </div>
                        <div className="transaction-summary-wrapper">
                            <div className="summary-wrapper">
                                <div className="summary-content">
                                    <div className="logo">
                                        {fundData?.fund_logo_url ?
                                            <img src={fundData?.fund_logo_url} alt="" className="logo" /> : <CiImageOn style={{ height: "80px", width: "80px" }} />}
                                    </div>
                                    <hr className="separator-h" />
                                    <h1 className="sub-title" style={{ alignSelf: "center" }}>{t('timeline').toUpperCase()}</h1>
                                    <hr className="separator-h" />
                                    <div className="timeline-root">
                                        {traceData.map((item) => (
                                            <div key={item?._id} className="timeline-item">
                                                <div className="timeline-separator">
                                                    <div className="timeline-dot"></div>
                                                    <div className="timeline-connector" />
                                                </div>
                                                <div className="timeline-content">
                                                    <div className="timeline-navbar">
                                                        <div className="avatar-container">
                                                            {item?.updated_by_photo_url_xx ?
                                                                <img src={item?.updated_by_photo_url_xx} alt="" className="avatar" /> : <CiImageOn style={{ width: "30px", height: "30px" }} />}
                                                            <span className="username">{item?.updated_by_name_xx}</span>
                                                        </div>
                                                        <span className="time-ago">{item?.time_ago_xx}</span>
                                                    </div>
                                                    <div className="remarks">
                                                        {item?.trc_remarks}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Dialog to notify user about changes being saved */}
            <Dialog open={isNotifyDialogOpen}
                onClose={() => setIsNotifyDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    },
                }}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: notifyDialogText }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton currentTheme={currentTheme} onClick={() => setIsNotifyDialogOpen(false)}>{t('close')}</CustomLoadingButton>
                </DialogActions>
            </Dialog>
            {isProcessing && (
                <motion.div
                    className="processing-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="processing-wrapper">
                        <GridLoader
                            color="#F44336"
                            style={{
                                height: "50px",
                                width: "50px",
                            }}
                        />
                    </div>
                </motion.div>
            )}
            {reportsDialogOpen && (
                <motion.div
                    className="overlay-dialog reports"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <span className="main-title">{t('reports')}</span>
                            <CircularButton currentTheme={currentTheme} onClick={() => setReportsDialogOpen(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <OnScreenReports
                                reportGroupArray={fundData?.report_keys_array}
                                documentData={{
                                    docNo: "",
                                    docType: "AM Fund",
                                    docId: fundDocId,
                                }} />
                        </div>
                    </div>
                </motion.div>)}


        </motion.div>
    );
}

export default AmMonitor